import { ODATA_BASE } from "helpers/api_config";
import React, { useEffect, useState, useMemo } from "react";
import { Container, Row, Col, Card, CardBody } from "reactstrap";
import Breadcrumbs from "components/Common/Breadcrumb";
import SayfaRehberi from "../../../components/Common/SayfaRehberi";
import TableContainer from "../../../components/Common/TableContainer";
import axios from "axios";

// Rapor yalnizca su alanlari kullaniyor. $select verilmezse OData butun
// satiri donduruyor; Users_Password ve Users_MailAddress de tarayiciya
// iniyordu (Moderation sayfasindaki daraltmanin aynisi).
const USERS_URL =
  `${ODATA_BASE}/Users?$select=ID,Users_Name_Surname,Users_Nickname,Users_PhoneNumber,Users_Points,Users_Referred_By`;

const ReferralReport = () => {
  document.title = "Referans Raporu | Berlin Esnaf";

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios
      .get(USERS_URL)
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : (res.data && res.data.value) || [];
        if (mounted) setUsers(data);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  // Kullanıcıyı ID ile hızlı bulmak için map
  const usersById = useMemo(() => {
    const m = {};
    users.forEach((u) => { m[String(u.ID)] = u; });
    return m;
  }, [users]);

  // Referans olanlar (Users_Referred_By dolu)
  const referredUsers = useMemo(
    () => users.filter((u) => u.Users_Referred_By && String(u.Users_Referred_By).trim() !== ""),
    [users]
  );

  // Getirenlere göre grupla -> liderlik tablosu
  const leaderboard = useMemo(() => {
    const map = {}; // referrerId -> { count }
    referredUsers.forEach((u) => {
      const code = String(u.Users_Referred_By).toUpperCase().trim();
      const refId = code.replace(/[^0-9]/g, "");
      if (!refId) return;
      if (!map[refId]) map[refId] = { count: 0 };
      map[refId].count += 1;
    });
    return Object.keys(map)
      .map((refId) => {
        const referrer = usersById[refId];
        return {
          refId,
          code: "BES" + refId,
          name: referrer ? (referrer.Users_Name_Surname || referrer.Users_Nickname || "-") : "(Silinmiş kullanıcı)",
          points: referrer && referrer.Users_Points != null ? referrer.Users_Points : 0,
          count: map[refId].count,
        };
      })
      .sort((a, b) => b.count - a.count);
  }, [referredUsers, usersById]);

  const totalReferred = referredUsers.length;
  const totalReferrers = leaderboard.length;

  const leaderboardColumns = useMemo(
    () => [
      { Header: "Referans Kodu", accessor: "code", filterable: true,
        Cell: (c) => <span className="badge badge-soft-primary font-size-12">{c.row.original.code}</span> },
      { Header: "Getiren Kişi", accessor: "name", filterable: true },
      { Header: "Davet Sayısı", accessor: "count", filterable: false,
        Cell: (c) => <span className="badge badge-soft-success font-size-12">{c.row.original.count} kişi</span> },
      { Header: "Puan", accessor: "points", filterable: false,
        Cell: (c) => <span className="badge badge-soft-warning font-size-12">{c.row.original.points}</span> },
    ],
    []
  );

  const referredColumns = useMemo(
    () => [
      { Header: "ID", accessor: "ID", filterable: false },
      { Header: "Yeni Üye", accessor: "Users_Name_Surname", filterable: true,
        Cell: (c) => <span>{c.row.original.Users_Name_Surname || c.row.original.Users_Nickname || "-"}</span> },
      { Header: "Telefon", accessor: "Users_PhoneNumber", filterable: true,
        Cell: (c) => <span>{c.row.original.Users_PhoneNumber || "-"}</span> },
      { Header: "Kullanılan Kod", accessor: "Users_Referred_By", filterable: true,
        Cell: (c) => <span className="text-success">{c.row.original.Users_Referred_By}</span> },
    ],
    []
  );

  const StatCard = ({ title, value, icon, color }) => (
    <Col md="4">
      <Card className="mini-stats-wid">
        <CardBody>
          <div className="d-flex">
            <div className="flex-grow-1">
              <p className="text-muted fw-medium mb-2">{title}</p>
              <h4 className="mb-0">{value}</h4>
            </div>
            <div className="avatar-sm rounded-circle bg-primary align-self-center mini-stat-icon">
              <span className={"avatar-title rounded-circle bg-" + color}>
                <i className={"bx " + icon + " font-size-24"} />
              </span>
            </div>
          </div>
        </CardBody>
      </Card>
    </Col>
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Berlin Esnaf" breadcrumbItem="Referans Raporu" />
          <SayfaRehberi sayfa="referralreport" />

          <Row>
            <StatCard title="Toplam Davet Edilen Üye" value={totalReferred} icon="bx-user-plus" color="success" />
            <StatCard title="Davet Eden Kişi Sayısı" value={totalReferrers} icon="bx-group" color="primary" />
            <StatCard title="Toplam Kullanıcı" value={users.length} icon="bx-user" color="info" />
          </Row>

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <h5 className="mb-3">🏆 En Çok Davet Edenler</h5>
                  {loading ? (
                    <p className="text-muted">Yükleniyor...</p>
                  ) : leaderboard.length === 0 ? (
                    <p className="text-muted">Henüz referansla kayıt olan kullanıcı yok.</p>
                  ) : (
                    <TableContainer
                      columns={leaderboardColumns}
                      data={leaderboard}
                      isGlobalFilter={true}
                      customPageSize={10}
                      className="custom-header-css"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <h5 className="mb-3">Referansla Kayıt Olan Üyeler</h5>
                  {loading ? (
                    <p className="text-muted">Yükleniyor...</p>
                  ) : referredUsers.length === 0 ? (
                    <p className="text-muted">Kayıt bulunamadı.</p>
                  ) : (
                    <TableContainer
                      columns={referredColumns}
                      data={referredUsers}
                      isGlobalFilter={true}
                      customPageSize={10}
                      className="custom-header-css"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ReferralReport;
